import { client } from "@open-api";
import { getURL } from "@utils/Common";
import axios, {
  AxiosRequestHeaders,
  AxiosResponseHeaders,
  RawAxiosResponseHeaders
} from "axios";
import { HttpStatus } from "@configs/HttpStatus";
import { REDIRECT_PARAM } from "@resources/Constants";
import { isKeycloakEnabled } from "@configs/Env";
import { keycloak } from "@auth/keycloak";
import { attachAuthHeaders } from "@auth/attachAuthHeaders";
import {
  refreshAuthToken,
  redirectToPlatformLogin,
  setAccessToken
} from "@auth/tokenManager";

let isRefreshing = false;
let refreshSubscribers: (() => void)[] = [];

const isJSONResponse = (
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders
) => {
  return (
    headers["content-type"] &&
    headers["content-type"].includes("application/json")
  );
};

client.setConfig({
  baseURL: `https://api.${getURL()}`,
  withCredentials: true
});
const instance = client.instance;

instance.interceptors.request.use(
  async (config) => {
    const newConfig = { ...config };
    newConfig.headers = {
      ...newConfig.headers
    } as AxiosRequestHeaders;
    // Keycloak 활성 시 Bearer 토큰 + 커스텀 헤더 주입 (비활성 시 그대로 반환).
    return attachAuthHeaders(newConfig);
  },
  (error) => {
    return Promise.reject(error);
  }
);

const isForbiddenUser = (errorResponseData: {
  message: string;
  error: string;
  statusCode: number;
}) => {
  return errorResponseData.message === "Forbidden resource";
};

instance.interceptors.response.use(
  (response) => {
    if (isJSONResponse(response.headers)) {
      return { ...response, data: response.data.data };
    }
    return { ...response, data: response.data };
  },
  async (error) => {
    if (error.response) {
      console.error(error.response.data);
      if (error.response.status === HttpStatus.UNAUTHORIZED) {
        const originalRequest = error.config;

        // Keycloak Bearer: 401 → 토큰 갱신 후 재시도. 실패 시 Platform 으로 이동.
        if (isKeycloakEnabled()) {
          if (originalRequest && !originalRequest.retry) {
            originalRequest.retry = true;
            try {
              const token = await refreshAuthToken(-1);
              if (token && keycloak.authenticated) {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${token}`
                };
                return instance(originalRequest);
              }
            } catch {
              setAccessToken(null);
            }
          }
          redirectToPlatformLogin({ bypassGuard: true });
          return Promise.reject(error);
        }

        // 비-Keycloak(로컬 개발) 폴백: 기존 서버 쿠키 refresh 흐름 유지.
        if (error.response?.data?.code === 401000) {
          redirectToPlatformLogin({ bypassGuard: true });
        }

        if (error.response?.data?.code === 401001 && !originalRequest.retry) {
          originalRequest.retry = true;

          if (!isRefreshing) {
            isRefreshing = true;
            instance
              .post(`/v1/auth/access-token/refresh`)
              .then(() => {
                isRefreshing = false;
                refreshSubscribers.forEach((callback) => {
                  callback();
                });
                refreshSubscribers = [];
              })
              .catch(() => {
                isRefreshing = false;

                redirectToPlatformLogin({ bypassGuard: true });
              });
          }

          return new Promise((res) => {
            refreshSubscribers.push(() => res(axios(originalRequest)));
          });
        }
      }
      if (
        error.response.status === HttpStatus.FORBIDDEN &&
        isForbiddenUser(error.response.data)
      ) {
        if (!/\/forbidden/.test(window.location.href)) {
          const currentUrl = window.location.href;
          window.location.href = `/forbidden?${REDIRECT_PARAM}=${currentUrl}`;
        }
      }
    }

    return Promise.reject(error);
  }
);
